import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { LinkedApplicationsField } from './components/LinkedApplicationsField'
import { UserOrNomineeListField } from './components/RequestFormFields'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Textarea } from '@/components/shadcn/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { applicationAssignmentSchema, userOrNomineeSchema } from '../Requests/schemas'
import {
  AddOrganisationTicket,
  ApplicationAccessBlock,
  isBlankPerson,
  isCompletePerson,
  PROJECT_FREQUENCY_OPTIONS,
  uniquePeople,
  UserOrNominee,
} from '../Requests/types'

const formSchema = z.object({
  organisationName: z.string().min(1, 'Organisation name is required'),
  longName: z.string().min(1, 'Long name is required'),
  description: z.string().min(1, 'Description is required'),
  domain: z.string().optional(),
  projectName: z.string().min(1, 'Project name is required'),
  frequency: z.enum(['Daily', 'Weekly', 'Monthly', 'Annually', 'Ad hoc']),
  projectLeaders: z
    .array(userOrNomineeSchema)
    .min(1, 'At least one project leader is required')
    .refine((people) => people.every(isCompletePerson), {
      message: 'Each project leader needs a given name, family name, and email',
    }),
  projectAdministrators: z
    .array(userOrNomineeSchema)
    .optional()
    .refine((people) => !people?.length || people.every((person) => isCompletePerson(person) || isBlankPerson(person)), {
      message: 'Finish or remove incomplete administrators',
    }),
  projectMembers: z
    .array(userOrNomineeSchema)
    .optional()
    .refine((people) => !people?.length || people.every((person) => isCompletePerson(person) || isBlankPerson(person)), {
      message: 'Finish or remove incomplete members',
    }),
  applications: z
    .array(
      z.object({
        applicationId: z.number(),
        assignments: z.array(applicationAssignmentSchema),
      })
    )
    .min(1, 'Select at least one linked application')
    .superRefine((apps, ctx) => {
      apps.forEach((app, index) => {
        if (!app.assignments.some((assignment) => assignment.roleIds.length > 0)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Assign at least one role to a nominated person',
            path: [index, 'assignments'],
          })
        }
      })
    }),
})

type FormValues = z.infer<typeof formSchema>

type RequestAddOrganisationFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTitle?: string
}

export function RequestAddOrganisationForm(props: RequestAddOrganisationFormProps) {
  const [sheetOpen, setSheetOpen] = useState(false)
  const onSubmit = useCallback(() => {
    props.onSubmit?.()
    setSheetOpen(false)
  }, [props])

  return props.sheetTrigger ? (
    <SheetWithFormComponent
      title={props.sheetTitle ?? 'Request new organisation'}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
      trigger={props.sheetTrigger}
    >
      <FormComponent {...props} onSubmit={onSubmit} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} onSubmit={onSubmit} />
  )
}

function FormComponent({ onSubmit }: RequestAddOrganisationFormProps & { onSubmit: () => void }) {
  const {
    getApplicationsApiGetApplications,
    putRequestsApiAddRequest,
    getRequestsApiGetPlatformRequests,
  } = useRestClient()

  const { data: applications } = getApplicationsApiGetApplications.useQuery(undefined)

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      organisationName: '',
      longName: '',
      description: '',
      domain: '',
      projectName: '',
      frequency: 'Monthly',
      projectLeaders: [{ nominee: { givenName: '', familyName: '', email: '' } }],
      projectAdministrators: [],
      projectMembers: [],
      applications: [],
    },
  })

  const applicationOptions = useMemo(
    () =>
      applications?.map((a) => ({
        value: a.applicationId!,
        label: a.appName ?? `App ${a.applicationId}`,
      })) ?? [],
    [applications]
  )

  const { mutate, isPending } = putRequestsApiAddRequest.useMutation(undefined, {
    onSuccess: () => {
      getRequestsApiGetPlatformRequests.invalidateQueries(undefined)
      toast.success('Add organisation request submitted')
      onSubmit()
    },
    onError: () => toast.error('Failed to submit add organisation request'),
  })

  const projectLeaders = (form.watch('projectLeaders') ?? []) as UserOrNominee[]
  const projectAdministrators = (form.watch('projectAdministrators') ?? []) as UserOrNominee[]
  const projectMembers = (form.watch('projectMembers') ?? []) as UserOrNominee[]
  const nominatedPeople = uniquePeople([...projectLeaders, ...projectAdministrators, ...projectMembers])

  const handleSubmit = form.handleSubmit(
    (values) => {
      const ticket: AddOrganisationTicket = {
        organisation: {
          name: values.organisationName,
          longName: values.longName,
          description: values.description,
          domain: values.domain ?? '',
        },
        projectName: values.projectName,
        frequency: values.frequency,
        projectLeaders: values.projectLeaders as UserOrNominee[],
        projectAdministrators: (values.projectAdministrators as UserOrNominee[] | undefined)?.filter(isCompletePerson),
        projectMembers: (values.projectMembers as UserOrNominee[] | undefined)?.filter(isCompletePerson),
        applications: (values.applications as ApplicationAccessBlock[]).map((block) => ({
          ...block,
          assignments: block.assignments.filter((assignment) => assignment.roleIds.length > 0),
        })),
      }
      mutate({
        body: {
          ticketType: 3,
          ticket: JSON.stringify(ticket),
        },
      })
    },
    () => {
      toast.error('Please complete project leaders, linked applications, and at least one application role')
    }
  )

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <FormField
          control={form.control}
          name="organisationName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organisation name *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="longName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Long name *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description *</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="domain"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Domain</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="projectName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Project name *</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="frequency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Frequency *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PROJECT_FREQUENCY_OPTIONS.map((freq) => (
                    <SelectItem key={freq} value={freq}>
                      {freq}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="projectLeaders"
          render={() => (
            <FormItem>
              <UserOrNomineeListField
                label="Project leaders"
                required
                userOptions={[]}
                nomineeOnly
                value={projectLeaders}
                onChange={(next) => form.setValue('projectLeaders', next, { shouldValidate: true })}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="projectAdministrators"
          render={() => (
            <FormItem>
              <UserOrNomineeListField
                label="Project administrators"
                userOptions={[]}
                nomineeOnly
                value={projectAdministrators}
                onChange={(next) => form.setValue('projectAdministrators', next, { shouldValidate: true })}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="projectMembers"
          render={() => (
            <FormItem>
              <UserOrNomineeListField
                label="Project members"
                userOptions={[]}
                nomineeOnly
                value={projectMembers}
                onChange={(next) => form.setValue('projectMembers', next, { shouldValidate: true })}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="applications"
          render={() => (
            <FormItem>
              <LinkedApplicationsField
                applicationOptions={applicationOptions}
                userOptions={[]}
                assignmentPeople={nominatedPeople}
                value={form.watch('applications') as ApplicationAccessBlock[]}
                onChange={(applications) => form.setValue('applications', applications, { shouldValidate: true })}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending}>
          Submit request
        </Button>
      </form>
    </Form>
  )
}
